import React from 'react';
import { Link } from 'react-router-dom';
import { COVERAGE_TYPES } from '../lib/coverage';

const TRUST_ITEMS = [
  { icon: '✅', label: 'Independent Agency' },
  { icon: '🤝', label: 'Multiple Carriers' },
  { icon: '⭐', label: 'Texas Licensed' },
  { icon: '🧭', label: 'Personalized Advice' },
  { icon: '⚡', label: 'Fast Quotes' },
  { icon: '🔒', label: 'Your information is never sold' },
];

const WHY_ITEMS = [
  { title: 'Independent Agency', desc: 'We work for you, not a single insurance company.' },
  { title: 'Multiple Carriers', desc: 'We compare options to help you find the right fit.' },
  { title: 'Personalized Guidance', desc: 'Real advice from real people who listen.' },
  { title: 'Fast Quotes', desc: 'Start with basic info — we take it from there.' },
  { title: 'Texas Licensed', desc: 'Locally licensed and trusted by Texas families.' },
  { title: 'Your information is never sold', desc: 'Used only to prepare your quote or review.' },
];

const STEPS = [
  { n: 1, title: 'Choose what you want to protect', desc: 'Pick a coverage type to get started.' },
  { n: 2, title: 'Start with basic information', desc: 'Just a few quick details — that\'s it.' },
  { n: 3, title: 'Complete your Protection Profile when ready', desc: 'Add more details for a more accurate review.' },
  { n: 4, title: 'We review your options', desc: 'Our licensed team compares carriers and coverage.' },
  { n: 5, title: 'You decide what works best', desc: 'No pressure. Friendly guidance every step.' },
];

const ProtectionCenter = () => {
  return (
    <div data-testid="protection-center-page">
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#01325B 0%, #02466e 50%, #E07A2C 140%)' }}>
        <div className="container mx-auto px-4 py-20 md:py-28 text-white">
          <div className="max-w-3xl">
            <div className="inline-block text-xs tracking-[0.25em] uppercase opacity-80 mb-4">The Protection Center</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Welcome to Your <br className="hidden sm:block" />
              <span style={{ color: '#FFD9B8' }}>Protection Center</span>
            </h1>
            <p className="text-lg md:text-xl opacity-90 mb-8 leading-relaxed">
              Insurance does not have to be complicated. We help Texas families, individuals, and business owners
              compare coverage options, understand their protection, and find solutions that fit their life and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#what-to-protect"
                data-testid="hero-cta-start"
                className="inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold text-base shadow-lg transition-transform hover:scale-[1.02]"
                style={{ background: '#E07A2C', color: 'white' }}
              >
                Start My Protection Profile →
              </a>
              <Link
                to="/contact"
                data-testid="hero-cta-question"
                className="inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold text-base border-2 border-white/70 hover:bg-white hover:text-[#01325B] transition-colors"
              >
                I Just Have a Question
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST ROW */}
      <section className="bg-[#f6f5f1] border-b border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
            {TRUST_ITEMS.map((t) => (
              <div key={t.label} className="flex flex-col items-center text-sm font-medium" style={{ color: '#01325B' }}>
                <span className="text-xl mb-1">{t.icon}</span>
                <span>{t.label}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-600 mt-4 italic">
            Your information is used only to prepare your insurance quote or coverage review. We do not sell your information.
          </p>
        </div>
      </section>

      {/* WHAT TO PROTECT */}
      <section id="what-to-protect" className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#01325B' }}>
              What would you like to protect?
            </h2>
            <p className="text-gray-700">
              Start simple. Pick a coverage type below — we'll guide you from there.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {COVERAGE_TYPES.map((c) => (
              <Link
                key={c.slug}
                to={`/protection-center/start/${c.slug}`}
                data-testid={`coverage-card-${c.slug}`}
                className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#E07A2C] hover:shadow-xl transition-all duration-200 flex flex-col"
              >
                <div className="text-4xl mb-3">{c.emoji}</div>
                <div className="text-lg font-bold mb-1" style={{ color: '#01325B' }}>{c.label}</div>
                <div className="text-sm text-gray-600 mb-4 flex-1">{c.blurb}</div>
                <div className="text-xs text-gray-500 italic mb-3">Start with basic info. Complete the full profile when ready.</div>
                <span className="text-sm font-semibold text-[#E07A2C] group-hover:translate-x-1 transition-transform">Start →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* START SIMPLE */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] mb-3 font-semibold" style={{ color: '#E07A2C' }}>Start Simple</div>
            <h2 className="text-3xl md:text-4xl font-bold mb-5" style={{ color: '#01325B' }}>
              We know sharing personal info is a big ask.
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">
              That is why our process lets you begin with only the basics. Once we know what you are looking for,
              you can choose to complete a full <strong>Protection Profile</strong> so we can provide a more accurate
              quote or coverage review.
            </p>
          </div>
        </div>
      </section>

      {/* WHY HCP */}
      <section className="py-16 bg-[#f6f5f1]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: '#01325B' }}>
            Why Here Comes Protection
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_ITEMS.map((w) => (
              <div key={w.title} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="font-bold text-lg mb-2" style={{ color: '#01325B' }}>{w.title}</div>
                <div className="text-gray-700 text-sm leading-relaxed">{w.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: '#01325B' }}>
            How It Works
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {STEPS.map((s) => (
              <div key={s.n} className="flex items-start gap-4 p-5 rounded-xl bg-[#f6f5f1] border border-gray-200">
                <div className="flex-shrink-0 w-10 h-10 rounded-full text-white font-bold flex items-center justify-center" style={{ background: '#E07A2C' }}>
                  {s.n}
                </div>
                <div>
                  <div className="font-bold text-base md:text-lg" style={{ color: '#01325B' }}>{s.title}</div>
                  <div className="text-sm text-gray-700 mt-1">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" data-testid="contact-section" className="py-16 scroll-mt-24" style={{ background: '#01325B' }}>
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-2">Here Comes Protection</h2>
          <p className="italic opacity-90 mb-8">Insured Right. Sleep Tight.</p>

          <div className="max-w-xl mx-auto space-y-3 mb-8">
            <p className="text-base">
              <span className="opacity-80">Email: </span>
              <a
                href="mailto:Herecomesprotection@gmail.com"
                data-testid="contact-email-link"
                className="font-semibold underline hover:text-[#FFD9B8]"
              >
                Herecomesprotection@gmail.com
              </a>
            </p>
            <p className="text-base">
              <span className="opacity-80">Website: </span>
              <a
                href="https://herecomesprotection.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-website-link"
                className="font-semibold underline hover:text-[#FFD9B8]"
              >
                herecomesprotection.com
              </a>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <a
              href="mailto:Herecomesprotection@gmail.com"
              data-testid="contact-email-cta"
              className="inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold text-base"
              style={{ background: '#E07A2C', color: 'white' }}
            >
              Email Us
            </a>
            <a
              href="https://calendly.com/g-garza-insure/herecomeprotection"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-schedule-call"
              className="inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold text-base border-2 border-white/70 hover:bg-white hover:text-[#01325B] transition-colors"
            >
              Schedule a Call
            </a>
          </div>

          <p className="max-w-2xl mx-auto opacity-80 text-xs italic">
            Your information is used only to prepare your insurance quote or coverage review. We do not sell your information.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ProtectionCenter;
